import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import Modal from './Modal';

const PdfSectionTextModal = ({
  isOpen,
  onClose,
  configId,
  sections = [], // Default to an empty array if sections is undefined
  sectionsText,
  onSave,
  onBack,
  pdfBuilderDocs,
}) => {
  // Loop through each section and check if each document exists for that section
  Object.keys(sectionsText).forEach((section) => {
    // Loop through each document in pdfBuilderDocs
    pdfBuilderDocs.forEach((doc) => {
      // Check if the document exists in the current section
      const exists = sectionsText[section].some(
        (entry) => Object.keys(entry)[0] === doc,
      );

      // If the document does not exist, add an empty entry for that document
      if (!exists) {
        sectionsText[section].push({ [doc]: '' });
      }
    });
  });

  const [sectionTexts, setSectionTexts] = useState(sectionsText || {});
  const [reviewedSections, setReviewedSections] = useState(
    Array.isArray(sections)
      ? sections.reduce((acc, section) => ({ ...acc, [section]: false }), {})
      : {},
  );
  const [selectedSection, setSelectedSection] = useState(sections[0] || '');
  const isSmallScreen = window.innerWidth <= 768;

  // Refs for scroll positions
  const pdfContentRef = useRef(null);
  const allSectionsReviewed = Object.values(reviewedSections).every(
    (isReviewed) => isReviewed,
  );

  useEffect(() => {
    if (sections.length > 0) {
      setSelectedSection(sections[0]); // Default to the first section on load
    }
  }, [sections]);

  useEffect(() => {
    // Only scroll to bottom when all sections are reviewed and no change in review status
    if (allSectionsReviewed && pdfContentRef.current) {
      pdfContentRef.current.scrollTo({
        top: pdfContentRef.current.scrollHeight, // Scroll to the bottom
        behavior: 'smooth', // Smooth scroll effect
      });
    }
  }, [allSectionsReviewed]); // This hook will run whenever `allSectionsReviewed` changes

  const handleSectionTextChange = (section, index, newText) => {
    setSectionTexts((prevSectionTexts) => ({
      ...prevSectionTexts,
      [section]: prevSectionTexts[section].map((textObj, i) =>
        i === index
          ? { ...textObj, [Object.keys(textObj)[0]]: newText }
          : textObj,
      ),
    }));
  };

  const markSectionAsReviewed = (section) => {
    setReviewedSections((prevReviewed) => ({
      ...prevReviewed,
      [section]: !prevReviewed[section],
    }));
  };

  const isSectionFilled = (section) => {
    // Check if all text areas in the selected section are filled
    return sectionTexts[section]?.every((textObj) => {
      const text = Object.values(textObj)[0]; // Extract text from object
      return text && text.trim() !== ''; // Check if it's not empty
    });
  };

  const handleMarkAsReviewed = (section) => {
    // Mark section as reviewed only if all text areas are filled
    if (isSectionFilled(section)) {
      markSectionAsReviewed(section); // Proceed to mark as reviewed
    } else {
      // Optionally show an alert or message that all fields must be filled
      alert(
        'Please fill all text areas before marking this section as reviewed.',
      );
    }
  };

  const handleSave = async () => {
    // Save trimmed data and close the modal or dialog
    await onSave(configId, sectionTexts);
    onClose();
  };

  const buttonStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    marginBottom: '0.5rem',
    padding: '0.75rem 1.25rem',
    border: 'none',
    boxShadow: 'none',
    borderColor: 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
  };

  const renderSectionButtons = () => {
    return sections.map((section) => {
      const isActive = selectedSection === section;
      const activeStyle = {
        backgroundColor: isActive ? '#6bd888' : '#f9f9f9',
        color: isActive ? 'white' : 'black',
      };

      return (
        <Button
          key={section}
          onClick={() => {
            setSelectedSection(section);

            // Reset scroll positions when section changes
            if (pdfContentRef.current) {
              pdfContentRef.current.scrollTo(0, 0);
            }
          }}
          style={{
            ...buttonStyle,
            ...activeStyle,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <input
            type="checkbox"
            checked={reviewedSections[section] || false}
            onChange={() => handleMarkAsReviewed(section)}
            style={{ marginRight: '10px' }}
          />
          {section}
        </Button>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      fullScreen={true}
      isCloseButton={false}
    >
      <div
        style={{
          textAlign: 'center',
          margin: '30px 0',
          height: '100%', // Ensure the modal takes full height
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '14px',
              width: '80%',
              height: '2px',
              backgroundColor: '#ccc',
              zIndex: 0,
            }}
          />
          {[
            'Section Extraction',
            'Content Extraction',
            'Instructions Generation',
          ].map((step, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                zIndex: 1,
                animation: `fadeInSlide 0.5s ${idx * 0.15}s ease forwards`,
                opacity: 0,
                margin: '0 10px', // Spacing between steps,
                cursor:
                  idx === 0 || (idx === 2 && allSectionsReviewed)
                    ? 'pointer'
                    : 'default',
              }}
              onClick={() => {
                if (idx === 0) {
                  onBack(); // Call onBack for the first step
                } else if (idx === 2 && allSectionsReviewed) {
                  handleSave(); // Call handleSave after reviewing all sections
                }
              }}
            >
              <div
                style={{
                  backgroundColor: idx > 1 ? '#6c757d' : '#6bd888', // Different colors for each step
                  color: '#fff',
                  width: '32px', // Slightly larger circle
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '500',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Subtle shadow for better contrast
                  transition: 'transform 0.2s ease, background-color 0.3s ease',
                  transform: idx === 1 ? 'scale(1.4)' : 'scale(1)', // Slight scaling for active step
                }}
              >
                {idx + 1}
              </div>
              <div
                style={{
                  fontSize: '13px', // Slightly larger font for better readability
                  marginTop: '5px',
                  color: idx > 1 ? '#333' : '#6bd888', // Active step text color
                }}
              >
                {step}
              </div>
            </div>
          ))}
        </div>
        <p
          style={{
            fontWeight: '900',
            fontSize: '15px',
            color: '#666',
            margin: '0 auto',
            backgroundColor: '#FFF3CD',
          }}
        >
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
            style={{ color: '#f76c6c', marginRight: '4px' }}
          />
          Carefully review the content—this final step will guide instruction
          generation. Update or add examples as needed before submitting.
        </p>
        <div
          style={{
            display: 'flex',
            height: '70vh',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            margin: '0 auto',
            flexWrap: 'wrap',
            flexDirection: isSmallScreen ? 'column' : 'row',
            overflow: 'hidden',
          }}
        >
          {/* Section Buttons */}
          <div
            className="scrollable-container"
            style={{
              display: 'flex',
              flex: isSmallScreen ? '0 1 auto' : '0.5',
              overflowX: 'scroll',
              overflowY: 'scroll',
              maxHeight: '100%',
              maxWidth: isSmallScreen ? '100%' : '200px',
              padding: '10px',
              borderBottom: isSmallScreen ? '1px solid #ccc' : 'unset',
              borderRight: !isSmallScreen ? '1px solid #ccc' : 'unset',
              flexDirection: isSmallScreen ? 'row' : 'column',
              whiteSpace: isSmallScreen ? 'nowrap' : 'unset',
            }}
          >
            {renderSectionButtons()}
          </div>

          <style>
            {`
              .scrollable-container::-webkit-scrollbar {
                width: 5px; /* Default width of vertical scrollbar */
                height: 5px; /* Adjusted height for horizontal scrollbar */
              }
              .scrollable-container::-webkit-scrollbar-thumb {
                background-color: #888;
                border-radius: 6px;
              }
              .scrollable-container::-webkit-scrollbar-thumb:hover {
                background-color: #555;
              }
              .scrollable-container::-webkit-scrollbar-track {
                background-color: #f0f0f0;
                border-radius: 6px;
              }
          `}
          </style>
          {/* Section Content */}
          <div
            className="scrollable-container"
            style={{
              flex: isSmallScreen ? '1' : '2',
              maxHeight: '500px',
              overflowY: 'scroll', // Always show vertical scrollbar
              padding: '10px',
            }}
            ref={pdfContentRef} // Add ref for PDF content scroll
          >
            {/* Review Button */}
            <Button
              color={reviewedSections[selectedSection] ? 'primary' : 'danger'}
              onClick={() => handleMarkAsReviewed(selectedSection)}
              style={{
                marginBottom: '10px',
                fontSize: isSmallScreen ? '0.9rem' : '0.8rem',
              }}
              size={isSmallScreen ? 'sm' : 'md'}
            >
              {reviewedSections[selectedSection]
                ? 'Reviewed'
                : 'Mark as Reviewed'}
            </Button>

            {/* Text Areas */}
            <div
              style={{
                fontSize: '16px',
                color: '#555',
                lineHeight: '1.5',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh', // Ensure the container takes up at least full height of the viewport
                paddingBottom: '80px', // Ensure space for button on regular screens
              }}
            >
              {sectionTexts[selectedSection] &&
                sectionTexts[selectedSection].map((textObj, index) => {
                  const [pdfName, text] = Object.entries(textObj)[0]; // Get PDF name and text
                  return (
                    <div
                      key={index}
                      style={{
                        marginBottom: '10px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <label
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '5px',
                          color: '#333',
                        }}
                      >
                        Content From PDF {pdfName}
                      </label>

                      <textarea
                        value={text || ''}
                        placeholder={
                          text
                            ? ''
                            : 'Content not found in the document. It may be missing or overlooked. Kindly provide an example by typing or copying and pasting relevant text to enhance instruction generation.'
                        }
                        onChange={(e) =>
                          handleSectionTextChange(
                            selectedSection,
                            index,
                            e.target.value,
                          )
                        }
                        style={{
                          border: `2px solid ${text ? '#6bd888' : '#dc3545'}`,
                          borderRadius: '8px',
                          padding: '10px',
                          resize: 'vertical',
                          minHeight: '100px',
                          maxHeight: '300px',
                        }}
                      />
                    </div>
                  );
                })}

              <Button
                color={allSectionsReviewed ? 'primary' : 'secondary'}
                onClick={handleSave}
                hidden={!allSectionsReviewed}
                style={{
                  marginTop: '20px', // Push the button to the bottom
                  padding: '10px 20px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                  bottom: '20px', // Space from the bottom of the viewport
                  left: '50%', // Center horizontally
                  transform: 'translateX(-50%)', // Adjust position to perfectly center
                }}
              >
                Generate Instructions
              </Button>
            </div>
          </div>

          <style>
            {`
              /* Styling for the scrollable container */
              .scrollable-container::-webkit-scrollbar {
                width: 6px; /* Adjust width for vertical scrollbar */
                height: 6px; /* Adjust height for horizontal scrollbar */
              }
              .scrollable-container::-webkit-scrollbar-thumb {
                background-color: #888;
                border-radius: 6px;
              }
              .scrollable-container::-webkit-scrollbar-thumb:hover {
                background-color: #555;
              }
              .scrollable-container::-webkit-scrollbar-track {
                background-color: #f0f0f0;
                border-radius: 6px;
              }

              /* Styling for textarea scrollbars */
              .scrollable-container textarea::-webkit-scrollbar {
                width: 6px; /* Adjust width for textarea vertical scrollbar */
                height: 6px; /* Adjust height for textarea horizontal scrollbar */
              }
              .scrollable-container textarea::-webkit-scrollbar-thumb {
                background-color: #888;
                border-radius: 6px;
              }
              .scrollable-container textarea::-webkit-scrollbar-thumb:hover {
                background-color: #555;
              }
              .scrollable-container textarea::-webkit-scrollbar-track {
                background-color: #f0f0f0;
                border-radius: 6px;
              }
            `}
          </style>
        </div>
      </div>
      {/* Animation Keyframes */}
      <style>
        {`
          @keyframes fadeInSlide {
            0% { opacity: 0; transform: translateY(-15px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Modal>
  );
};

export default PdfSectionTextModal;
